/* eslint-disable @typescript-eslint/no-explicit-any */

// Imports for external libraries go here.
import React, { FC, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import clsx from 'clsx';
import { useApolloClient } from '@apollo/client';
import { EditableComponent } from '@adobe/aem-react-editable-components';

// Imports for internal (to the monorepo) libraries go here,
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { Button, Messages, PillCollection, TabComponent } from '@marriott/mi-ui-library';
import { useSession } from '@marriott/mi-headless-utils';
import { isNonEmptyString } from '@marriott/shared/mi-helper-utils';

// The closer the import is to the file the lower it should be in this list.
import {
  DEFAULT_FILTERS_ORDER,
  FILTERS_MAPPING,
  MAPS_FILTERS_URL_PARAMS_MAPPING,
  MOCK_DATA_TYPES,
  PROPERTIES_LIMIT,
  QUERY_PARAM_MAPPING,
  TAB_ICONS,
} from '../../modules/constants/constant';
import {
  phoenixLuxurySearchByGeoQueryMap,
  phoenixLuxurySearchByGeoQueryMapFilters,
  phoenixLuxurySearchByLocationMap,
  phoenixLuxurySearchByLocationMapFilters,
} from '../../molecules/MapView/queries';
import {
  fetchHDData,
  filterAvailableFilters,
  filterPillAvailable,
  FiltersPillSelection,
  getDescriptionByCode,
  getDisplayedFilters,
  getFilteredOrder,
  parallelDynamicCalls,
  totalFilterCount,
  updateFiltersPayload,
  useViewport,
  buildFilterPriorityMap,
  updateURLParams,
  constructPayloadFromUrl,
} from '../../modules/helpers';
import { SelectedFiltersBar } from './SelectedFiltersBar/SelectedFiltersBar';
import { SearchResultsFilterPanel } from '../../molecules/SearchResultsFilterPanel';
import { MapView } from '../../molecules/MapView';
import { SearchResultsComponentProps } from './SearchResultsComponent.types';
import { StyledSearchResultsComponent } from './SearchResultsComponent.styles';
import { ListView } from '../../molecules/ListView';
import { shouldUseMockData } from '../../modules/utils/helpers';

// Use named rather than default exports.
export const SearchResultsComponent: FC<SearchResultsComponentProps> = props => {
  const client = useApolloClient();
  const { session }: any = useSession();

  const clientEnvVars = useClientEnvVarsStore.getState().envVarsObject;
  const { ACCEPT_LANGUAGE: currentLocale, REQUEST_ID: requestID } = clientEnvVars;

  const viewPort = useViewport();

  /**
   * REF'S HERE......
   */

  const mapRef = useRef<google.maps.Map | null>(null);
  const didCenterRef = useRef(false);

  /**
   * STATES HERE......
   */

  const [popupOpenState, setPopupOpenState] = useState<boolean>(false);

  const { tabList, apiFailuerErrorMessage } = props;
  // state to set the list/map view
  const [selectedTab, setSelectedTab] = useState(tabList[0]?.tabValue || '');

  // State for pills data - initially empty, will be populated by useEffect
  const [pillsData, setPillsData] = useState<any[]>([]);

  // State now stores only the list of searchResults, defaulting to an empty array
  const [searchResults, setSearchResults] = useState<any>([]);

  const [facets, setFacets] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState(true);

  const [isError, setIsError] = useState('');

  /**
   * serves as the payload to the UXL queries...
   */
  const [filtersPayload, setFiltersPayload] = useState<any>(() => updateFiltersPayload(props));

  /**
   * Flag to track if session has been initialized to prevent double API calls
   */
  const [isSessionInitialized, setIsSessionInitialized] = useState<boolean>(false);

  //state to identify if the brand is clicked or not
  const [brandsClicked, setBrandsClicked] = useState<boolean>(false);

  //State to show the selected filter pills on ui....
  const [selectedFilters, setSelectedFilters] = useState<any>([]);

  const [showSearchHereBtn, setShowSearchHereBtn] = useState<any>(false);

  const [isGeoQuerySearch, setIsGeoQuerySearch] = useState<any>(false);

  /**
   * a state variable to set the center of the map which further used for the Geo Query search....
   */

  const [mapCenter, setMapCenter] = useState<any>({ lat: 0, lng: 0 });

  //Tracks which filter pill is clicked
  const [filterButtonClicked, setFilterButtonClicked] = useState<string>('');

  /**
   * a state variable to set the default region from the data layer response....
   */
  const [defaultRegion, setDefaultRegion] = useState<string>(props?.regions?.fallbackRegion);

  // Check if Author mode is enabled or not.
  const isAuthorModeEnabled = props?.isAuthorMode ?? false;

  /**
   * Function to toggle the active tab
   * @param tabValue - The value of the tab to toggle
   * @returns void
   */
  const toggleActiveComponents = useCallback(
    (tabValue: string) => {
      const params = new URLSearchParams(window.location.search);
      params.set('view', tabValue);
      setSelectedTab(tabValue);
      window.history.replaceState({}, '', `?${params}`);
    },
    [setSelectedTab]
  );

  /** Key for named exports on the dynamic SearchResults mock module. */
  type SearchResultsMockExportKey =
    | typeof MOCK_DATA_TYPES.PROPERTIES_MOCK_DATA
    | typeof MOCK_DATA_TYPES.FACETS_MOCK_DATA;

  /**
   * Loads GraphQL-shaped mock data for author/dev flows without bundling the large mock file upfront.
   * When `shouldUseMockData(isAuthorMode)` is false, returns `{}` (call sites still choose mock vs `fetchHDData` using author mode).
   *
   * @param isAuthorMode - Whether AEM author mode is active; gated with `shouldUseMockData` for mock usage.
   * @param key - Which export to read from `./__mock__/SearchResultsComponent.model` (`propertiesMockData` for list/map results, `facetsMockData` for filter facets).
   * @returns The selected mock export, or `{}` when mocks must not be used.
   */
  const getSearchResultsMock = useCallback(
    async (isAuthorMode: boolean, key: SearchResultsMockExportKey) => {
      if (!shouldUseMockData(isAuthorMode)) return {};
      const mockModule = await import('./__mock__/SearchResultsComponent.model');
      return mockModule[key as unknown as keyof typeof mockModule] as any;
    },
    [isAuthorModeEnabled]
  );

  /***
   * Capturing the facets response ....
   */
  const getFacetsData = async (query: any, variables: any) => {
    const data = isAuthorModeEnabled
      ? await getSearchResultsMock(isAuthorModeEnabled, MOCK_DATA_TYPES.FACETS_MOCK_DATA as SearchResultsMockExportKey)
      : await fetchHDData(client, query, {
          variables,
          context: {
            headers: {
              'x-request-id': requestID,
              'accept-language': currentLocale?.replace('_', '-'),
            },
          },
        });

    const searchResultsFacets =
      data?.search?.properties?.searchByLocation?.facets ?? data?.search?.properties?.searchByGeolocation?.facets ?? [];

    if (searchResultsFacets?.length === 0) {
      setIsError('noData');
    }

    if (!data) {
      setIsError('requestFailed');
    }

    setFacets(searchResultsFacets);
  };

  /***
   * fetching all the records based on the max Limit and the total avl. properties .....
   */
  const fetchAllRecords = async ({ query, baseVariables }: { query: any; baseVariables: any }) => {
    const offset = 0;
    let total = 0;

    let allEdges: any[] = [];

    /**
     * setting the loading state to true to display the loader ....
     */
    setIsLoading(true);

    try {
      /***
       * forming the payload...
       */
      const variables = {
        ...baseVariables,
        limit: PROPERTIES_LIMIT,
        offset,
      };

      /**
       * Making the UXL call with the payload ....
       */
      const data = isAuthorModeEnabled
        ? await getSearchResultsMock(
            isAuthorModeEnabled,
            MOCK_DATA_TYPES.PROPERTIES_MOCK_DATA as SearchResultsMockExportKey
          )
        : await fetchHDData(client, query, {
            variables,
            context: {
              headers: {
                'x-request-id': requestID,
                'accept-language': currentLocale?.replace('_', '-'),
              },
            },
          });

      const searchNode =
        data?.search?.properties?.searchByLocation ?? data?.search?.properties?.searchByGeolocation ?? [];

      const edges = searchNode?.edges ?? [];

      //Storing the total properties count which in turn useful for the further parallel calls....
      total = searchNode?.total;

      /**
       * appending the new edge's response to the existing edge's....
       */
      allEdges = [...allEdges, ...edges];

      //calculating further parallel calls limit...
      const parallelCallsLimit = Math.floor(total / PROPERTIES_LIMIT);

      /**
       * Making the subsequent calls parallel.....
       */
      if (parallelCallsLimit > 0) {
        const res: any = await parallelDynamicCalls(
          parallelCallsLimit,
          query,
          baseVariables,
          client,
          requestID,
          currentLocale
        );

        res?.map((data: any) => {
          const searchRes =
            data?.search?.properties?.searchByLocation ?? data?.search?.properties?.searchByGeolocation ?? [];

          const edgesRes = searchRes?.edges ?? [];

          /**
           * appending the new edge's response to the existing edge's....
           */
          allEdges = [...allEdges, ...edgesRes];
        });
      }

      /**
       * Setting the final edges response in the state....
       */
      setSearchResults(allEdges);
    } catch {
      setIsError('requestFailed');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  //getting priority list for selected filters order
  const priorityList: Record<string, number> = useMemo(() => {
    return buildFilterPriorityMap(props?.filtersOrderList);
  }, [props?.filtersOrderList]);

  /**
   * Merge URL + session into component state (filters, tab, geo from URL).
   */
  useEffect(() => {
    const hasSession = session && Object.keys(session).length > 0;
    /** AEM author / preview often has no session; still load mocks so the map is not stuck on isLoading. */
    if (!hasSession && !isAuthorModeEnabled) return;

    let location = '';
    if (hasSession && !props?.regions?.defaultViewFlag) {
      const akamaiLoc = session?.data?.akamaiHeader
        ? session?.data?.akamaiHeader
        : session?.cacheData?.data?.akamaiHeader;
      const regex = /country_code=([^,]+)/;
      const loc = akamaiLoc?.match(regex);

      location = loc && loc[1] ? loc[1] : '';
    }

    let selectedRegionName = props?.regions?.fallbackRegion;

    if (isNonEmptyString(location)) {
      selectedRegionName = props?.regionMappings?.[location]
        ? props?.regionMappings?.[location]
        : props?.regions?.fallbackRegion;
    }

    setDefaultRegion(selectedRegionName);

    /**
     * Getting the query params from URL too perform deeplinking....
     */

    const url = window.location.search;

    const initialFilters = updateFiltersPayload(props, isGeoQuerySearch, selectedRegionName);

    /**
     * forming the payload from the page URL....
     */

    const filtersPayloadFromParams = constructPayloadFromUrl(
      url,
      initialFilters,
      MAPS_FILTERS_URL_PARAMS_MAPPING,
      true
    );

    /**
     * setting the brandsClicked flag true when there are brands avl. in the URL ...
     */

    const brandsIsClicked = url?.includes('marriottBrands') && props?.brandTags?.length > 1;

    setBrandsClicked(!!brandsIsClicked);

    // calculating the filters count from queryparams
    const filtersParamCount = totalFilterCount(filtersPayloadFromParams, !!brandsClicked, props);

    const payload = typeof filtersParamCount === 'number' ? filtersPayloadFromParams : initialFilters;

    setFiltersPayload(payload);
    setIsSessionInitialized(true);

    setShowSearchHereBtn(false);

    const params = new URLSearchParams(url);

    const urlViewParam = params?.get('view') ?? selectedTab;

    urlViewParam && setSelectedTab(urlViewParam);

    if (params?.get('latitude') || params?.get('longitude')) {
      const latitude = Number(params?.get('latitude'));
      const longitude = Number(params?.get('longitude'));
      setMapCenter({ lat: latitude, lng: longitude });
      setIsGeoQuerySearch(true);
    }
  }, [session, isAuthorModeEnabled]);

  /**
   * Refetch when filters or geo-search mode change locally (session no longer updates on filter apply).
   * Intentionally omits mapCenter so panning does not refetch until the user applies "Search this area"
   * (which updates filtersPayload).
   */
  useEffect(() => {
    const hasSession = session && Object.keys(session).length > 0;
    if (!hasSession && !isAuthorModeEnabled) return;

    // Prevent API call until session has initialized the filtersPayload
    if (!isSessionInitialized) return;

    const url = window.location.search;
    const params = new URLSearchParams(url);
    const distance = Number(props?.searchRadius);

    let latitude = mapCenter.lat;
    let longitude = mapCenter.lng;
    let isGeoSearch = isGeoQuerySearch;

    if (params?.get('latitude') || params?.get('longitude')) {
      latitude = Number(params?.get('latitude'));
      longitude = Number(params?.get('longitude'));
      isGeoSearch = true;
    }

    const variables = {
      search: {
        options: { numberInParty: 1 },
        facets: {
          terms: [...filtersPayload],
        },
      },
    };

    const locationMapFiltersVariables = {
      search: {
        ...variables.search,
        options: { ...variables.search.options, countsOnly: true },
      },
    };

    const geoQueryVariables = {
      search: {
        latitude: latitude,
        longitude: longitude,
        distance: distance,
        distanceUnit: 'MILES',
        options: {
          numberInParty: 1,
        },
        facets: {
          terms: [...filtersPayload],
        },
      },
    };

    if (isGeoSearch) {
      fetchAllRecords({
        query: phoenixLuxurySearchByGeoQueryMap,
        baseVariables: geoQueryVariables,
      });
      getFacetsData(phoenixLuxurySearchByGeoQueryMapFilters, geoQueryVariables);
    } else {
      fetchAllRecords({
        query: phoenixLuxurySearchByLocationMap,
        baseVariables: variables,
      });
      getFacetsData(phoenixLuxurySearchByLocationMapFilters, locationMapFiltersVariables);
    }
  }, [filtersPayload, session, isSessionInitialized]);

  const handleCenterChange = useCallback(() => {
    if (!didCenterRef.current) {
      didCenterRef.current = true;
      return;
    }
    const center = mapRef.current?.getCenter();
    const newLat = center?.lat();
    const newLng = center?.lng();
    const mCenter = { lat: newLat, lng: newLng };
    setMapCenter(mCenter);
    setShowSearchHereBtn(true);
  }, []);

  const searchThisArea = () => {
    const payload = filtersPayload?.map((filter: { type: string }) => {
      if (filter.type === FILTERS_MAPPING.Regions) {
        return {
          type: FILTERS_MAPPING.Regions,
          dimensions: [],
        };
      } else if (filter.type === FILTERS_MAPPING?.City) {
        return {
          type: FILTERS_MAPPING?.City,
          dimensions: [],
        };
      } else if (filter.type === FILTERS_MAPPING?.State) {
        return {
          type: FILTERS_MAPPING?.State,
          dimensions: [],
        };
      } else if (filter.type === FILTERS_MAPPING?.Country) {
        return {
          type: FILTERS_MAPPING?.Country,
          dimensions: [],
        };
      } else {
        return filter;
      }
    });

    payload?.forEach(({ type, dimensions }: { type: string; dimensions: string[] }) => {
      const paramKey = QUERY_PARAM_MAPPING[type];
      const hasMultipleBrands = props?.brandTags?.length > 1;

      // Handle the specific brand length check
      if ((type === 'BRANDS' && hasMultipleBrands && dimensions.length !== props?.brandTags?.length) || !paramKey)
        return;

      updateURLParams(paramKey, dimensions, false, hasMultipleBrands, true, mapCenter);
    });

    setFiltersPayload(payload);
    setIsGeoQuerySearch(true);
    setShowSearchHereBtn(false);

    if (didCenterRef.current) {
      didCenterRef.current = false;
    }
  };

  /**
   *
   * @param filterType | filter type of the clicked filter
   * @param filterCode | clicked filter code, helps to compare with the filter's payload
   * so as to reset the respective filter type payload to default and to trigger the UXL...
   */
  const handleRemoveFilter = (filterType: string, filterCode: string) => {
    let brandPillClicked = brandsClicked;
    const newFiltersPayload = filtersPayload?.map((filterGroup: { type: string; dimensions: Array<string> }) => {
      if (filterGroup?.type === filterType) {
        const newDimensions = filterGroup?.dimensions?.filter((code: string) => code !== filterCode);
        const payload = updateFiltersPayload(props);

        if (filterType === FILTERS_MAPPING.Brands && props?.brandTags?.length > 0 && newDimensions?.length === 0) {
          setBrandsClicked(false);
          brandPillClicked = false;

          return {
            ...filterGroup,
            dimensions: payload[0]?.dimensions ?? [],
          };
        } else if (
          filterType === FILTERS_MAPPING.Amenities &&
          props?.amenityTags?.length > 0 &&
          newDimensions?.length === 0
        ) {
          return {
            ...filterGroup,
            dimensions: payload[1]?.dimensions ?? [],
          };
        } else if (
          filterType === FILTERS_MAPPING.Property &&
          props?.propertyTypeTags?.length > 0 &&
          newDimensions?.length === 0
        ) {
          return {
            ...filterGroup,
            dimensions: payload[2]?.dimensions ?? [],
          };
        } else if (
          filterType === FILTERS_MAPPING.Activities &&
          props?.activityTags?.length > 0 &&
          newDimensions?.length === 0
        ) {
          return {
            ...filterGroup,
            dimensions: payload[3]?.dimensions ?? [],
          };
        }
        return {
          ...filterGroup,
          dimensions: newDimensions,
        };
      }
      return filterGroup;
    });

    newFiltersPayload?.forEach(({ type, dimensions }: { type: string; dimensions: string[] }) => {
      const paramKey = QUERY_PARAM_MAPPING[type];
      const hasMultipleBrands = props?.brandTags?.length > 1;

      // Handle the specific brand length check
      if (
        (type === 'BRANDS' &&
          hasMultipleBrands &&
          dimensions.length !== props?.brandTags?.length &&
          !brandPillClicked) ||
        !paramKey
      )
        return;

      updateURLParams(paramKey, dimensions, brandPillClicked, hasMultipleBrands, isGeoQuerySearch, mapCenter);
    });
    setFiltersPayload(newFiltersPayload);
  };

  /**
   * Function to handle the pill click
   * @param value - The value of the pill to click
   * @returns void
   */
  const handlePillClick = useCallback(
    (value: string) => {
      !popupOpenState ? setFilterButtonClicked(value) : setFilterButtonClicked('');
      setPopupOpenState(!popupOpenState);
    },
    [popupOpenState, setFilterButtonClicked, setPopupOpenState]
  );

  /** AEM author often omits labels; publish should use authored copy only. */
  const authorLabel = (text: string | undefined, fallback: string) => (isAuthorModeEnabled ? text ?? fallback : text);

  type FilterPillData = {
    label: string | undefined;
    value: string;
    clickTrackVal: string | undefined;
  };

  /**
   * Mapping's to map the filter pills
   */
  const filterDataMap: Record<string, FilterPillData> = {
    BRANDS: {
      label: authorLabel(props?.brands?.brandsLabel, 'Brands'),
      value: 'Brands',
      clickTrackVal: props?.amenitiesClickTrackValue,
    },
    LEISURE_REGIONS: {
      label: authorLabel(props?.regions?.regionsLabel, 'Regions'),
      value: 'Regions',
      clickTrackVal: props?.regionClickTrackValue,
    },
    LOCATION: {
      label: authorLabel(props?.regions?.locationLabel, 'Location'),
      value: 'Location',
      clickTrackVal: props?.locationClickTrackValue,
    },
    AMENITIES: {
      label: authorLabel(props?.amenities?.amenitiesLabel, 'Amenities'),
      value: 'Amenities',
      clickTrackVal: props?.amenitiesClickTrackValue,
    },
    ACTIVITIES: {
      label: authorLabel(props?.activities?.activitiesLabel, 'Activities'),
      value: 'Activities',
      clickTrackVal: props?.activitiesClickTrackValue,
    },
    PROPERTY_TYPES: {
      label: authorLabel(props?.propertyTypeLabel, 'Property'),
      value: 'Property',
      clickTrackVal: props?.propertyTypeClickTrackValue,
    },
  };

  // useEffect to populate pills data from filters prop
  useEffect(() => {
    if (!facets || facets.length === 0) return;

    const displayFiltersCount = totalFilterCount(filtersPayload, brandsClicked, props);

    const filtersForDisplay =
      facets?.length > 0 &&
      displayFiltersCount > 0 &&
      getDisplayedFilters(filtersPayload, facets, brandsClicked, props);

    setSelectedFilters(filtersForDisplay);

    const pillData = [
      {
        label: props?.filterLabel,
        value: 'AllFilters',
        isActive: displayFiltersCount > 0,
        showIcon: true,
        iconClass: 'icon-dining-filter',
        count: displayFiltersCount,
        custom_click_track_value: props?.filtersClickTrackValue,
        buttonClassName: 'd-flex align-items-center all-filters-button',
        id: 'all filters',
        className: 'custom_click_track',
      },
    ];

    if (viewPort !== 'viewPortL') {
      setPillsData(pillData);
    } else {
      const filtersOrderList =
        props?.filtersOrderList?.length > 0
          ? getFilteredOrder(props, props?.filtersOrderList, isAuthorModeEnabled)
          : getFilteredOrder(props, DEFAULT_FILTERS_ORDER, isAuthorModeEnabled);

      /**
       * forming the availabel pills response based on the list of filter's in filtersOrderList....
       */
      const newPillsData = filtersOrderList?.map(
        (pill: { filterName: string; label: string; value: string; clickTrackVal: string }) => {
          const filterName = pill?.filterName;
          const filterInfo = filterDataMap?.[filterName];

          //To identify weather the filter's are applied or not for the respective filter pill...
          const isSelected = FiltersPillSelection(filtersPayload, filterName, props, brandsClicked);

          /**
           * Checks if the pill is has the data in it or not
           * returns false if it doesn't has the response with in the pill and vice versa....
           */
          const isPillAvailable = filterPillAvailable(facets, filterName);

          const responseMap = new Map(facets?.map(response => [response?.type?.enumCode, response]));
          const response = responseMap?.get(filterName);

          const propTypeDataAvl = filterAvailableFilters(response?.buckets, props?.propertyTypeTags || [])?.length > 0;

          const iconDirection = filterButtonClicked === filterInfo?.value ? 'icon-arrow-up' : 'icon-arrow-down';

          //filtering the regions response
          const regionFacets = facets?.filter(
            (filterObj: { type: { enumCode: string } }) => FILTERS_MAPPING.Regions === filterObj?.type?.enumCode
          );

          const pillLabel =
            isGeoQuerySearch && filterName === FILTERS_MAPPING.Regions ? props?.regions?.regionsLabel : '';

          //To populate the selected region Value with in the pill...
          const regionValue = getDescriptionByCode(
            regionFacets,
            filtersPayload[filtersPayload?.length - 1]?.dimensions[0],
            pillLabel
          );

          return {
            label:
              filterName === FILTERS_MAPPING.Regions
                ? regionValue
                : isAuthorModeEnabled
                ? filterInfo?.label ?? filterInfo?.value ?? ''
                : filterInfo?.label,
            value: filterInfo?.value,
            isActive: isSelected,
            showIcon: true,
            iconClass: iconDirection,
            custom_click_track_value: filterInfo?.clickTrackVal,
            buttonClassName: clsx(
              'filter-pill',
              filterName === FILTERS_MAPPING.Brands ||
                filterName === FILTERS_MAPPING.Activities ||
                (filterName === FILTERS_MAPPING.Property ? propTypeDataAvl : isPillAvailable)
                ? 'd-flex'
                : 'd-none'
            ),
            id: pill.filterName,
            className: 'custom_click_track',
          };
        }
      );

      setPillsData(newPillsData);
    }
  }, [viewPort, facets, filterButtonClicked, isAuthorModeEnabled]);

  const getRegionFacets = useCallback(() => {
    const regionFacets = facets?.filter(
      (filterObj: { type: { enumCode: string } }) => FILTERS_MAPPING.Regions === filterObj?.type?.enumCode
    );
    return regionFacets;
  }, [facets]);

  const totalCards = searchResults?.length ?? 0;

  return (
    <StyledSearchResultsComponent
      $isAuthorModeEnabled={isAuthorModeEnabled}
      className={clsx(props?.styleclass, 'm-container-fullbleed')}
    >
      <div className="container">
        <h2 className="t-tilte-m">{props?.headerText}</h2>
        <div className="mb-2 d-flex justify-content-between">
          {facets?.length > 0 ? (
            <PillCollection
              onChange={value => handlePillClick(value)}
              customClass={clsx(
                'pill-collection d-flex',
                props?.styleclass?.includes('phoenix-luxury') ? 'luxury-pills' : ''
              )}
              pills={pillsData}
            />
          ) : (
            <div></div>
          )}
          <TabComponent
            selectedTab={selectedTab}
            tabList={props?.tabList}
            setSelectedTab={(value: string) => toggleActiveComponents(value)}
            tabContainerClass={`m-icon-tab-wrapper px-0 px-md-5`}
            customClass={`m-icon-tab-list m-switcher-tab-list mr-2`}
            iconClass={clsx('m-icon-tab-list-item m-switcher-tab-list-item col-sm-6 col-md-auto')}
            isSwitcherTabs={true}
            isIconTabs={true}
            clickTrackingLoc={'Maps'}
            tabIcon={TAB_ICONS}
            classTabsEnabled="icons-tabs-container"
            ariaRole="link"
          />
        </div>

        {selectedFilters && selectedFilters?.length > 0 && totalCards > 0 && (
          <SelectedFiltersBar
            selectedFilters={selectedFilters}
            onRemoveFilter={handleRemoveFilter}
            popupOpenState={popupOpenState}
            priorityList={priorityList}
          />
        )}

        {isError === 'noData' && !(totalCards > 0) && (
          <Messages
            messageType={'info'}
            className={'pb-3 px-2'}
            messageText={props?.noResultsErrorMessage}
            linkText={props?.errorLinkTextLabel}
            AlertLinkTextLabel={props?.errorLinkTextLabel}
            setLinkClick={() => {
              const payload = updateFiltersPayload(props, false, defaultRegion);
              setIsGeoQuerySearch(false);
              setShowSearchHereBtn(false);
              setBrandsClicked(false);
              setFiltersPayload(payload);
              payload?.forEach(({ type, dimensions }: { type: string; dimensions: string[] }) => {
                const paramKey = QUERY_PARAM_MAPPING[type];
                const hasMultipleBrands = props?.brandTags?.length > 1;

                // Handle the specific brand length check
                if (
                  (type === 'BRANDS' && hasMultipleBrands && dimensions.length !== props?.brandTags?.length) ||
                  !paramKey
                )
                  return;

                updateURLParams(paramKey, dimensions, false, hasMultipleBrands, false, mapCenter);
              });
            }}
          />
        )}
        {isError === 'requestFailed' && !isAuthorModeEnabled && (
          <Messages
            messageType="error-sev1"
            className={'pb-3 px-2'}
            messageText={apiFailuerErrorMessage || 'Could not load the map'}
          />
        )}

        {selectedTab === 'map' ? (
          <div className="map-container">
            {totalCards > 0 && (
              <div className="my-3 t-font-s fw-bold">
                {totalCards} {totalCards === 1 ? props?.propertyLabel : props?.propertiesLabel}
              </div>
            )}

            <MapView
              markers={searchResults}
              handleCenterChange={handleCenterChange}
              isLoading={isLoading}
              onMapReady={map => {
                mapRef.current = map;
              }}
              ctaLabel={props?.ctaLabel}
              showReviews={props?.showReviews}
              viewPort={viewPort}
              requestID={requestID}
              currentLocale={currentLocale}
              client={client}
              mapCenter={mapCenter}
              isAuthorModeEnabled={isAuthorModeEnabled}
            />

            {showSearchHereBtn && (
              <div className={clsx('search-here-btn-container d-flex align-items-center justify-content-center')}>
                <Button
                  className="m-button-gallery search-here-btn"
                  aria-hidden
                  tabIndex={-1}
                  buttonCopy="Search This Area"
                  callback={searchThisArea}
                  isTextBeforeChildren={false}
                  children={<span className="icon icon-search"></span>}
                />
              </div>
            )}
          </div>
        ) : (
          <ListView
            ctaLabel={props?.ctaLabel}
            showReviews={props?.showReviews}
            previousLabel={props?.previousLabel}
            nextLabel={props?.nextLabel}
            filtersPayload={filtersPayload}
            mapCenter={mapCenter}
            isGeoQuerySearch={isGeoQuerySearch}
            requestID={requestID}
            currentLocale={currentLocale}
            client={client}
            getRegionFacets={getRegionFacets}
            propertyLabel={props?.propertyLabel}
            propertiesLabel={props?.propertiesLabel}
            reviewsLabel={props?.reviewsLabel}
            searchRadius={props?.searchRadius}
            isAuthorModeEnabled={isAuthorModeEnabled}
            viewPort={viewPort}
          />
        )}

        {popupOpenState && totalCards > 0 && (
          <SearchResultsFilterPanel
            filterProps={props}
            facets={facets}
            filtersPayload={filtersPayload}
            popupOpenState={popupOpenState}
            setPopupOpenState={setPopupOpenState}
            brandsClicked={brandsClicked}
            setBrandsClicked={setBrandsClicked}
            filterButtonClicked={filterButtonClicked}
            setFilterButtonClicked={setFilterButtonClicked}
            defaultRegion={defaultRegion}
            viewPort={viewPort}
            isGeoQuerySearch={isGeoQuerySearch}
            setIsGeoQuerySearch={setIsGeoQuerySearch}
            mapCenter={mapCenter}
            setFiltersPayload={setFiltersPayload}
            isAuthorModeEnabled={isAuthorModeEnabled}
          />
        )}
      </div>
    </StyledSearchResultsComponent>
  );
};

export const SearchResultsConfig = {
  emptyLabel: 'SearchResultsComponent',
  isEmpty: () => true,
  resourceType: 'mi-aem-luxury-spa/components/content/map-clusters/v1/map-clusters',
};

//TODO:Need to fix the lint issue for editable component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SearchResultsComponentEditable = (props: any) => (
  <div data-testid="luxury-SearchResultsComponent" data-component-name="o-luxury-SearchResultsComponent">
    <EditableComponent config={SearchResultsConfig} {...props}>
      <SearchResultsComponent {...props} />
    </EditableComponent>
  </div>
);
